import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { finalize, takeUntil } from 'rxjs/operators';
import { RestService, AccountEmailMap, IS_NOT_NULL_OR_UNDEFINED, User } from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';

import { BaseComponent } from '@shared/base/base.component';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { FILTERABLE_STATES } from '@domain/constants/filterable-states';
import { PAGE_SIZE } from '@domain/constants/app-variables';

@Component({
	selector: 'app-account-email-map-dialog',
	templateUrl: './account-email-map-dialog.component.html',
	styleUrls: ['./account-email-map-dialog.component.scss'],
})
export class AccountEmailMapDialogComponent extends BaseComponent implements OnInit, OnDestroy {
	isRequestInProgress: boolean = false;
	accountUsers: User[] = [];
	isLoadingUsersList = false;
	selectedUser!: User;

	/**
	 * carrier recruiter log form
	 */
	accountEmailMapForm: UntypedFormGroup = new UntypedFormGroup({
		state: new UntypedFormControl(null, [Validators.required]),
		accountId: new UntypedFormControl(null, [Validators.required]),
		userId: new UntypedFormControl(null, [Validators.required]),
	});

	stateOptions = FILTERABLE_STATES();

	constructor(
		private restService: RestService,
		private urlService: UrlService,
		private utilService: UtilService,
		public dialogRef: MatDialogRef<AccountEmailMapDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		super();
	}

	ngOnInit(): void {
		this.accountEmailMapForm.get('accountId').setValue(this.data.accountId);
		if (this.data.editEmail && this.data.accountEmailMap) {
			this.accountEmailMapForm.patchValue(this.data.accountEmailMap);
			this.accountEmailMapForm.get('state').setValue(this.data.accountEmailMap.state.split(','));
			this.accountEmailMapForm.get('userId').setValue(this.data.accountEmailMap?.c4UserDTO?.id);
			this.selectedUser = this.data.accountEmailMap?.c4UserDTO;
		}
		this.getAllActiveAccountUsers();
	}

	submitFormData(): void {
		if (this.accountEmailMapForm.invalid) {
			return;
		}
		if (this.data.addEmail) {
			this.addAccountEmailMap();
		} else if (this.data.editEmail) {
			this.editAccountEmailMap();
		}
	}

	addAccountEmailMap(): void {
		this.isRequestInProgress = true;
		const url = this.urlService.createAccountEmailMap();
		const formValue = this.accountEmailMapForm.value;
		const payload = {
			c4UserDTO: this.selectedUser,
			state: formValue.state.join(','),
			accountId: formValue.accountId,
		};
		this.restService
			.POST(url, payload)
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.isRequestInProgress = false;
				})
			)
			.subscribe(
				(response: any) => {
					this.utilService.showSnackbar('Email added', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
					this.closeDialog(response.data);
				},
				(error) => {
					this.utilService.showSnackbar('Cannot add email', SNACKBAR.ERROR, SNACKBAR.ACTION);
				}
			);
	}

	editAccountEmailMap(): void {
		this.isRequestInProgress = true;
		const url = this.urlService.updateAccountEmailMap();
		const formValue = this.accountEmailMapForm.value;
		let payload = Object.assign({}, this.data.accountEmailMap);
		payload.c4UserDTO = this.selectedUser;
		payload.state = formValue.state.join(',');
		this.restService
			.PUT(url, payload)
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.isRequestInProgress = false;
				})
			)
			.subscribe(
				(response: any) => {
					this.utilService.showSnackbar('Email updated', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
					this.closeDialog(response.data);
				},
				(error) => {
					this.utilService.showSnackbar('Unable to update email.', SNACKBAR.ERROR, SNACKBAR.ACTION);
				}
			);
	}

	getAllActiveAccountUsers() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.data.accountId)) {
			this.isLoadingUsersList = true;
			const url = this.urlService.getAllAccountUsers(
				this.data.accountId,
				0,
				PAGE_SIZE.USERS_LIST_CACHE
			);
			this.restService
				.GET(url)
				.pipe(
					takeUntil(this.isDestroyed$),
					finalize(() => {
						this.isLoadingUsersList = false;
					})
				)
				.subscribe((response) => {
					this.accountUsers = response.data.sort(
						(a, b) => 0 - (a.email.toLowerCase() > b.email.toLowerCase() ? -1 : 1)
					);
				});
		}
	}

	updateSelectedUser(userId: number): void {
		const selectedUser: User = this.accountUsers.find((activeUser) => activeUser.id === userId);
		if (selectedUser) {
			this.selectedUser = selectedUser;
			this.accountEmailMapForm.get('userId').setValue(this.selectedUser.id);
		}
	}

	closeDialog(data: any = null): void {
		this.dialogRef.close(data);
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
